import React, { useContext, useMemo } from 'react';
import { Navigate, Route } from 'react-router';
import StakeHolders from 'components/Dashboard/StakeHolders';
import ListRoutes from 'components/Dashboard/ListRoutes';
import EmailPerformance from 'components/Dashboard/Influence/EmailPerformance/EmailPerformance';
import CrmEmails from 'components/Dashboard/Influence/CrmEmails';
import CrmContacts from 'components/Dashboard/Influence/CrmContacts';
import UpdateCustomContact from 'components/Dashboard/Influence/CrmContacts/UpdateCustomContact/UpdateCustomContact';
import EmailComponent from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent';
import { store } from 'components/Store';
import { useAuth0 } from '@auth0/auth0-react';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import { InfluenceWritingAssistant } from 'components/Dashboard/Influence';
import CrmGroups from 'components/Dashboard/Influence/CrmGroups/CrmGroups';
import GroupView from 'components/Dashboard/Influence/CrmGroups/GroupView';
import CrmInteractions from 'components/Dashboard/Influence/CrmInteractions';
import CrmInteractionDetail from 'components/Dashboard/Influence/CrmInteractions/CrmInteractionDetail';

const useInfluenceRoutesMapping = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;
  const { enableStakeholders, enableCrm, teamName, code } = activePlan ?? {};

  const { isAuthenticated, user } = useAuth0();

  const hideInfluenceSection =
    parliamentarianUsers(code) || (teamName === 'NCA' && user.email !== 'james.mitchell@nca.gov.uk');

  // Only rebuild this (and the ~8 route element trees inside it) when something that actually
  // affects it changes, instead of on every render of the app.
  const InfluenceRoutesMapping = useMemo(
    () => ({
      'have-your-voice-heard': {
        element: <Navigate replace to={`/brief-policymakers/lobbying-material`} />,
      },
      'stakeholder-mapping': {
        element: hideInfluenceSection ? (
          <Navigate replace to={`/policymakers`} />
        ) : !enableStakeholders && isAuthenticated ? (
          <Navigate replace to={`/influence`} />
        ) : (
          <StakeHolders />
        ),
      },
      directory: {
        element: !enableCrm ? <Navigate replace to={`/influence`} /> : <CrmContacts />,
        parameters: [
          {
            param: 'update/:id',
            static: true,
            element: !enableCrm ? <Navigate replace to={`/influence`} /> : <UpdateCustomContact />,
          },
        ],
      },
      'my-groups': {
        element: !enableCrm ? <Navigate replace to={`/influence`} /> : <CrmGroups />,
        parameters: [
          {
            param: ':groupId',
            static: true,
            element: !enableCrm ? <Navigate replace to={`/influence`} /> : <GroupView />,
          },
        ],
      },
      emails: {
        element: !enableCrm ? <Navigate replace to={`/influence`} /> : <CrmEmails />,
        parameters: [
          {
            // if it can be static word along with dynamic param pass it with coma
            param: 'create,:crmEmailId',
            element: !enableCrm ? <Navigate replace to={`/influence`} /> : <EmailComponent />,
          },
        ],
      },
      'email-performance': {
        element: !enableCrm ? <Navigate replace to={`/influence`} /> : <EmailPerformance />,
        parameters: [
          {
            param: 'crmEmailId',
            element: !enableCrm ? <Navigate replace to={`/influence`} /> : <EmailPerformance />,
          },
        ],
      },
      interactions: {
        element: !enableCrm ? <Navigate replace to={`/influence`} /> : <CrmInteractions />,
        parameters: [
          {
            param: 'interactionId',
            element: !enableCrm ? <Navigate replace to={`/influence`} /> : <CrmInteractionDetail />,
          },
        ],
      },
      stakeholders: {
        element: <ListRoutes />,
      },
      'ai-assistant': {
        element: <InfluenceWritingAssistant />,
      },
    }),
    [hideInfluenceSection, enableStakeholders, enableCrm, isAuthenticated]
  );

  const createParameters = (parameters, element) => {
    let parameter = '';
    let elements = [];
    for (let i = 0; i < parameters.length; i++) {
      // if it can be static word along with dynamic param pass it with coma
      if (parameters[i]?.param?.includes(',')) {
        parameters[i]?.param.split(',').forEach((parameter) => {
          elements.push(
            <Route path={parameter} key={`route-${i}-${parameter}`} element={parameters[i]?.element ?? element} />
          );
        });
      } else {
        const newParam = parameters[i];
        parameter += `${parameter === '' ? '' : '/'}${newParam?.static ? '' : ':'}${newParam?.param ?? newParam}`;
        elements.push(<Route path={parameter} key={`route-${i}`} element={newParam?.element ?? element} />);
      }
    }
    return elements;
  };

  return { InfluenceRoutesMapping, createParameters };
};

export default useInfluenceRoutesMapping;
